<script setup lang="ts">
    import { useMediaQuery } from '@vueuse/core';

    const props = defineProps<{
        open?: boolean;
    }>();

    const emit = defineEmits<{
        close: [];
    }>();

    const isDesktop = useMediaQuery('(min-width: 1024px)', { ssrWidth: 1024 });
    const filterRef = useTemplateRef<HTMLElement>('filterRef');

    const { stickyStyle } = useBidirectionalSticky({
        enabled: isDesktop,
        topOffset: 160,
        bottomOffset: 16,
        elementRef: filterRef,
        transition: 'top 1s ease-in-out'
    });

    const { pending, filterState, visibleFilters, prettyError, showActions, toggleCheckboxOption, resetFilters } =
        useAcademyTrainingsListing();

    const filterContentProps = computed(() => ({
        pending: pending.value,
        error: prettyError.value,
        filters: visibleFilters.value,
        filterState: filterState.value
    }));

    function applyFilters() {
        if (!isDesktop.value) {
            emit('close');
        }
    }

    function resetAndClose() {
        resetFilters();

        if (!isDesktop.value) {
            emit('close');
        }
    }
</script>

<template>
    <aside
        ref="filterRef"
        :class="{ 'filter-panel': true, open: props.open }"
        :style="stickyStyle"
    >
        <h2 class="title">{{ $t('academy.listing.header') }}</h2>
        <ClientOnly>
            <div class="filter">
                <AcademyTrainingsListingFilterContent
                    v-bind="filterContentProps"
                    @toggle-option="toggleCheckboxOption"
                />
                <AcademyTrainingsListingFilterActions
                    v-if="showActions"
                    @apply="applyFilters"
                    @reset="resetFilters"
                />
            </div>
            <template #fallback>
                <div class="loading-skeleton --block"></div>
            </template>
        </ClientOnly>
    </aside>
    <ClientOnly>
        <BasicModal
            :is-open="props.open"
            full-screen
            @close="emit('close')"
        >
            <template #header>
                <h2>{{ $t('academy.listing.header') }}</h2>
            </template>
            <template #content>
                <div class="filter">
                    <AcademyTrainingsListingFilterContent
                        v-bind="filterContentProps"
                        @toggle-option="toggleCheckboxOption"
                    />
                </div>
            </template>
            <template #footer>
                <AcademyTrainingsListingFilterActions
                    v-if="showActions"
                    @apply="applyFilters"
                    @reset="resetAndClose"
                />
            </template>
        </BasicModal>
        <template #fallback>
            <!-- do not render anything till loaded -->
        </template>
    </ClientOnly>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/loading-skeleton.scss';

    .title {
        font-size: var(--font-m);
        font-weight: 700;
        margin: var(--spacing) 0;
    }

    .filter {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        padding: var(--spacing);
        background-color: var(--brand-primary-10);

        align-self: start;
        width: 100%;
        min-height: 100%;

        @media (min-width: $br-l) {
            border-radius: var(--border-radius-s);
            margin-bottom: var(--spacing);
            margin-top: var(--spacing);
        }
    }

    .filter-panel {
        display: none;

        @media (min-width: $br-l) {
            display: block;
        }
    }
</style>
